import { useEffect, useMemo, useRef, useState } from 'react'
import { X, ZoomIn, ZoomOut } from 'lucide-react'
import { useProjectStore, findPageById } from '../store/projectStore.js'
import { renderPageToDataURL } from '../lib/pdfjs.js'

export default function PageViewerModal({ pageId, onClose }) {
  const documents = useProjectStore((s) => s.documents)
  const page = useMemo(() => findPageById(documents, pageId), [documents, pageId])
  const doc = useMemo(() => {
    for (const d of documents) {
      if (d.pages.some((p) => p.id === pageId)) return d
    }
    return null
  }, [documents, pageId])

  const [zoom, setZoom] = useState(1)
  const [dataUrl, setDataUrl] = useState(null)
  const containerRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function run() {
      if (!doc || !page) return
      const { dataUrl } = await renderPageToDataURL(
        doc.id,
        doc.bytes,
        page.originalIndex + 1,
        { maxWidth: 1800 }
      )
      if (!cancelled) setDataUrl(dataUrl)
    }
    run()
    return () => {
      cancelled = true
    }
  }, [doc, page])

  if (!page) return null

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/90 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2 text-slate-200">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {doc?.originalName || 'Page'} — page {page.originalIndex + 1}
          </p>
          <p className="text-[11px] text-slate-400">
            {Math.round(page.width)} × {Math.round(page.height)} pt
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))}
            className="rounded-md p-1.5 hover:bg-slate-800"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="w-12 text-center text-xs tabular-nums text-slate-400">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(8, z * 1.25))}
            className="rounded-md p-1.5 hover:bg-slate-800"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="rounded-md px-2 py-1 text-[11px] hover:bg-slate-800"
          >
            Fit
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 rounded-md p-1.5 hover:bg-slate-800"
            aria-label="Close viewer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        className="relative flex flex-1 items-center justify-center overflow-auto p-4"
        onClick={(e) => {
          if (e.target === containerRef.current) onClose()
        }}
      >
        {dataUrl ? (
          <img
            src={dataUrl}
            alt={`Page ${page.originalIndex + 1}`}
            className="max-h-full select-none shadow-2xl"
            draggable={false}
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
          />
        ) : (
          <div className="flex items-center gap-2 text-slate-300">
            <div className="h-5 w-5 animate-spin-slow rounded-full border-2 border-slate-600 border-t-brand-400" />
            Rendering…
          </div>
        )}
      </div>
    </div>
  )
}
